import React from 'react';
import { useTranslation } from 'react-i18next';
import { patchResponsiveMobile, patchResponsiveMobileBox, hasMobileLayoutOverrides, getColumnMobileOverrides, clearMobileLayoutOverrides } from '../utils/responsiveLayout';
import { BREAKPOINT_MOBILE_MAX } from '../constants/responsiveBreakpoints';

const SliderControl = ({ label, value, onChange, min = 0, max = 200 }) => (
  <div className="bc-slider-row">
    <div className="bc-slider-label">{label}</div>
    <input type="range" className="bc-slider" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} />
    <input type="number" className="bc-slider-value" value={value} onChange={(e) => onChange(e.target.value)} min={min} max={max} />
  </div>
);

const ToggleSwitch = ({ label, value, onChange }) => (
  <div className="bc-toggle-wrapper" style={{ marginBottom: '12px' }}>
    <button type="button" className={`bc-toggle-btn no ${!value ? 'active' : ''}`} onClick={() => onChange(false)}>Não</button>
    <button type="button" className={`bc-toggle-btn yes ${value ? 'active' : ''}`} onClick={() => onChange(true)}>Sim</button>
    <span style={{ fontSize: '0.85rem', color: '#475569', marginLeft: '8px' }}>{label}</span>
  </div>
);

/**
 * Overrides de layout mobile (linha ou coluna) — Fase 1.
 */
export default function LayoutMobileOverridesPanel({ variant, content, onContentChange, editBreakpoint, embedded = false, showFooterCenter = false }) {
  const { t } = useTranslation('builder');
  const mobile = content?.responsive?.mobile || {};
  const colMobile = variant === 'column' ? getColumnMobileOverrides(content) : {};

  const apply = (next) => onContentChange(next);

  const updateMobile = (patch) => apply(patchResponsiveMobile(content, patch));

  const updateMobileBox = (boxKey, side, val) => apply(patchResponsiveMobileBox(content, boxKey, side, val));

  const customized = hasMobileLayoutOverrides(content);
  const isEditingMobile = editBreakpoint === 'mobile';

  return (
    <div
      style={{
        marginBottom: embedded ? 0 : '1rem',
        paddingBottom: embedded ? 0 : '1rem',
        borderBottom: embedded ? 'none' : '1px solid #e2e8f0',
        background: isEditingMobile ? '#fff7ed' : customized ? '#f0fdf4' : 'transparent',
        borderRadius: isEditingMobile || customized ? '8px' : 0,
        padding: isEditingMobile || customized ? '12px' : 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: embedded && !customized ? 0 : '10px' }}>
        {embedded ? <span /> : (
          <span className="bc-label" style={{ margin: 0 }}>
            {t('builder:mobileLayoutSectionTitle', { defaultValue: 'Ajustes mobile' })} (≤{BREAKPOINT_MOBILE_MAX}px)
          </span>
        )}
        {customized ? (
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 999 }}>
            {t('builder:mobileLayoutCustomized', { defaultValue: 'Personalizado' })}
          </span>
        ) : null}
      </div>
      {isEditingMobile ? (
        <p className="bc-hint" style={{ marginTop: 0, marginBottom: '10px' }}>
          {t('builder:mobileLayoutEditingHint', { defaultValue: 'O palco está em modo mobile — estes valores aplicam-se em telas pequenas.' })}
        </p>
      ) : (
        <p className="bc-hint" style={{ marginTop: 0, marginBottom: '10px' }}>
          {t('builder:mobileLayoutHint', { defaultValue: 'Opcional. Deixe em branco para herdar o desktop (ou regras automáticas).' })}
          {(variant === 'column' && colMobile.centerOnSmall) || (showFooterCenter && mobile.centerOnMobile) ? (
            <span style={{ display: 'block', marginTop: '6px', color: '#c2410c', fontWeight: 600 }}>
              {t('builder:mobileCenterPreviewHint', { defaultValue: 'Ative o modo Celular na barra superior para ver a centralização no palco.' })}
            </span>
          ) : null}
        </p>
      )}

      <ToggleSwitch
        label={t('builder:hideOnMobile', { defaultValue: 'Ocultar no celular' })}
        value={!!(variant === 'column' ? colMobile.hideOnMobile : mobile.hideOnMobile)}
        onChange={(v) => updateMobile({ hideOnMobile: v })}
      />

      {showFooterCenter && (
        <ToggleSwitch
          label={t('builder:footerCenterOnMobile', { defaultValue: 'Centralizar itens no celular' })}
          value={!!mobile.centerOnMobile}
          onChange={(v) => updateMobile({ centerOnMobile: v })}
        />
      )}

      {variant === 'row' && (
        <>
          <p className="bc-label" style={{ fontSize: '0.8rem', marginBottom: '6px' }}>{t('builder:mobileRowMargin', { defaultValue: 'Margem externa (mobile)' })}</p>
          <SliderControl label="Top" value={mobile.marginObj?.top ?? 0} onChange={(v) => updateMobileBox('marginObj', 'top', v)} min={-150} max={200} />
          <SliderControl label="Bottom" value={mobile.marginObj?.bottom ?? 0} onChange={(v) => updateMobileBox('marginObj', 'bottom', v)} min={-150} max={200} />
          <SliderControl label="Left" value={mobile.marginObj?.left ?? 0} onChange={(v) => updateMobileBox('marginObj', 'left', v)} min={-150} max={200} />
          <SliderControl label="Right" value={mobile.marginObj?.right ?? 0} onChange={(v) => updateMobileBox('marginObj', 'right', v)} min={-150} max={200} />
          <p className="bc-label" style={{ fontSize: '0.8rem', margin: '12px 0 6px' }}>{t('builder:mobileRowPadding', { defaultValue: 'Padding interno (mobile)' })}</p>
          <SliderControl label="Top" value={mobile.paddingObj?.top ?? 0} onChange={(v) => updateMobileBox('paddingObj', 'top', v)} />
          <SliderControl label="Bottom" value={mobile.paddingObj?.bottom ?? 0} onChange={(v) => updateMobileBox('paddingObj', 'bottom', v)} />
          <SliderControl label="Left" value={mobile.paddingObj?.left ?? 0} onChange={(v) => updateMobileBox('paddingObj', 'left', v)} />
          <SliderControl label="Right" value={mobile.paddingObj?.right ?? 0} onChange={(v) => updateMobileBox('paddingObj', 'right', v)} />
        </>
      )}

      {variant === 'column' && (
        <>
          <ToggleSwitch
            label={t('builder:columnCenterSmall', { defaultValue: 'Centralizar conteúdo em telas pequenas' })}
            value={!!colMobile.centerOnSmall}
            onChange={(v) => updateMobile({ centerOnSmall: v })}
          />
          <div className="bc-form-group">
            <label className="bc-label">{t('builder:columnOrderSmall', { defaultValue: 'Ordem em telas pequenas' })} ({colMobile.orderSmall ?? 0})</label>
            <input
              type="range"
              className="bc-slider"
              min={0}
              max={12}
              value={colMobile.orderSmall ?? 0}
              onChange={(e) => updateMobile({ orderSmall: parseInt(e.target.value, 10) })}
              style={{ width: '100%', accentColor: 'var(--primary)' }}
            />
            <span className="bc-hint">{t('builder:columnOrderSmallHint', { defaultValue: '0 = ordem padrão; valores maiores aparecem depois (max 767px).' })}</span>
          </div>
          <SliderControl
            label={t('builder:columnPaddingTop', { defaultValue: 'Padding superior (mobile)' })}
            value={colMobile.paddingTop ?? 0}
            onChange={(v) => updateMobile({ paddingTop: parseInt(v, 10) || 0 })}
            min={0}
            max={120}
          />
          <SliderControl
            label={t('builder:columnPaddingBottom', { defaultValue: 'Padding inferior (mobile)' })}
            value={colMobile.paddingBottom ?? 0}
            onChange={(v) => updateMobile({ paddingBottom: parseInt(v, 10) || 0 })}
            min={0}
            max={120}
          />
        </>
      )}

      {customized ? (
        <button
          type="button"
          className="bc-btn-cancel"
          style={{ marginTop: '10px', fontSize: '0.8rem', padding: '6px 12px' }}
          onClick={() => apply(clearMobileLayoutOverrides(content))}
        >
          {t('builder:mobileLayoutClear', { defaultValue: 'Limpar ajustes mobile' })}
        </button>
      ) : null}
    </div>
  );
}
